import { type FC, useMemo } from "react";
import { AppImage } from "@components/NextImage";
import { motion } from "motion/react";
import { cn } from "utils";
import {
  INTRO2_CYCLE_S,
  INTRO2_ED_CENTER_OPACITY,
  INTRO2_ED_CENTER_SCALE,
  INTRO2_ED_GLOW_OPACITY,
  INTRO2_ED_GLOW_SCALE,
  INTRO2_ORBIT_INNER,
  INTRO2_ORBIT_OUTER,
  INTRO2_SWEEP_BASE_DEG,
  INTRO2_TAG_MOTION,
  INTRO2_TAG_SCALE_IN,
  type Intro2TagKey,
  RADAR_SWEEP_DURATION_S,
} from "../introMotionPresets";
import { SIGN_IN_INTRO_ASSETS } from "../introSlidesConfig";
import {
  createSignInIntroCopy,
  type SignInIntroCopy,
} from "../signInIntroCopy";
import type { IntroHeroProps } from "../types";
import { useIntroIsActive } from "../IntroActiveIndexContext";
import { usePrefersReducedMotion } from "../usePrefersReducedMotion";
import { useParams } from "next/navigation";

type IntroSlide2Layout = "mobile" | "desktop";

interface IntroSlide2RadarProps extends Omit<IntroHeroProps, "isActive"> {
  /** H5 全宽 / PC 右栏视觉层 */
  layout?: IntroSlide2Layout;
}

const DOMAIN_TAGS: Array<{
  key: Intro2TagKey;
  icon: string;
  mobileClassName: string;
  desktopClassName: string;
}> = [
  {
    key: "cash",
    icon: SIGN_IN_INTRO_ASSETS.domainCash,
    mobileClassName: "left-[27%] top-[4%]",
    desktopClassName: "left-[26%] top-[3%]",
  },
  {
    key: "investing",
    icon: SIGN_IN_INTRO_ASSETS.domainInvesting,
    mobileClassName: "right-[4%] top-[28%]",
    desktopClassName: "right-[3%] top-[27%]",
  },
  {
    key: "decisions",
    icon: SIGN_IN_INTRO_ASSETS.domainDecisions,
    mobileClassName: "right-[14%] bottom-[14%]",
    desktopClassName: "right-[13%] bottom-[12%]",
  },
  {
    key: "wealth",
    icon: SIGN_IN_INTRO_ASSETS.domainWealth,
    mobileClassName: "left-[0%] bottom-[6%]",
    desktopClassName: "left-[0%] bottom-[5%]",
  },
];

const cycleEase = ["linear", "easeOut", "linear"] as const;

export const IntroSlide2Radar: FC<IntroSlide2RadarProps> = ({
  layout = "mobile",
}) => {
  const isActive = useIntroIsActive(1);
  const reducedMotion = usePrefersReducedMotion();
  const { locale = "en" } = useParams() as { locale?: string };
  const tags = useMemo(
    () => createSignInIntroCopy(locale).intro2.tags,
    [locale],
  );
  const isDesktop = layout === "desktop";
  const play = isActive && !reducedMotion;

  return (
    <div
      className={cn(
        "relative mx-auto aspect-square w-full",
        isDesktop ? "max-w-[530px]" : "max-w-[320px]",
      )}
      data-testid="intro-slide-2-radar"
      data-node-id={isDesktop ? "96344:109832" : "96301:11269"}
      data-layout={layout}
    >
      {/*
        Layer (bottom → top) per Figma + bug「tag 最上层」:
        Radar scanner → Ed glow → Orbit rings → Ed center → Domain tags (z-20)
      */}

      {/* Radar scanner — Figma 96344:109833 / 96301:11270；光束全程不停转 */}
      <motion.div
        className="absolute inset-[8%] z-[1] flex items-center justify-center"
        data-node-id={isDesktop ? "96344:109834" : "96301:11271"}
        data-testid="intro-slide-2-scanner"
        initial={{ rotate: INTRO2_SWEEP_BASE_DEG }}
        animate={
          play
            ? { rotate: INTRO2_SWEEP_BASE_DEG + 360 }
            : { rotate: INTRO2_SWEEP_BASE_DEG }
        }
        transition={
          play
            ? {
                duration: RADAR_SWEEP_DURATION_S,
                repeat: Infinity,
                ease: "linear",
              }
            : { duration: 0 }
        }
      >
        <div
          className="size-full rounded-full"
          style={{
            background:
              "conic-gradient(from 0deg, rgba(242,255,135,0.35) 0deg, rgba(242,255,135,0.12) 55deg, transparent 110deg, transparent 360deg)",
          }}
        />
        {/* Beam edge — 与扫光同组旋转 */}
        <div
          className="absolute left-1/2 top-1/2 h-0.5 w-[50%] origin-left -translate-y-1/2 bg-gradient-to-r from-[rgba(242,255,135,0.6)] to-transparent"
          data-name="Beam edge"
        />
      </motion.div>

      {/* Ed glow — Figma 96344:109836 */}
      <motion.div
        className={cn(
          "pointer-events-none absolute left-1/2 top-1/2 z-[2] -translate-x-1/2 -translate-y-1/2 rounded-full",
          isDesktop ? "size-[44%]" : "size-[47%]",
        )}
        data-node-id={isDesktop ? "96344:109836" : "96301:11273"}
        data-testid="intro-slide-2-ed-glow"
        style={{
          background:
            "radial-gradient(circle, rgba(242,255,135,0.42) 0%, rgba(242,255,135,0.08) 55%, transparent 72%)",
        }}
        initial={{ opacity: 0, scale: 1 }}
        animate={
          play
            ? {
                opacity: INTRO2_ED_GLOW_OPACITY.keyframes,
                scale: INTRO2_ED_GLOW_SCALE.keyframes,
              }
            : { opacity: 0.9, scale: 1 }
        }
        transition={
          play
            ? {
                opacity: {
                  duration: INTRO2_CYCLE_S,
                  times: INTRO2_ED_GLOW_OPACITY.times,
                  ease: "easeOut",
                  repeat: Infinity,
                },
                scale: {
                  duration: INTRO2_CYCLE_S,
                  times: INTRO2_ED_GLOW_SCALE.times,
                  ease: "easeInOut",
                  repeat: Infinity,
                },
              }
            : { duration: 0 }
        }
      />

      {/* Orbit rings — Figma 96344:109837 / 109838 */}
      <motion.div
        className="pointer-events-none absolute inset-[4%] z-[3] rounded-full border border-dashed border-content_inverse_primary/60"
        data-testid="intro-slide-2-orbit-outer"
        data-node-id={isDesktop ? "96344:109837" : "96301:11274"}
        initial={{ opacity: 0, scale: 0.7 }}
        animate={
          play
            ? {
                opacity: INTRO2_ORBIT_OUTER.opacity,
                scale: INTRO2_ORBIT_OUTER.scale,
              }
            : { opacity: 1, scale: 1 }
        }
        transition={
          play
            ? {
                duration: INTRO2_CYCLE_S,
                times: INTRO2_ORBIT_OUTER.times,
                ease: [...cycleEase],
                repeat: Infinity,
              }
            : { duration: 0 }
        }
      />
      <motion.div
        className="pointer-events-none absolute inset-[20%] z-[3] rounded-full border border-dashed border-content_inverse_primary/50"
        data-testid="intro-slide-2-orbit-inner"
        data-node-id={isDesktop ? "96344:109838" : "96301:11275"}
        initial={{ opacity: 0, scale: 0.7 }}
        animate={
          play
            ? {
                opacity: INTRO2_ORBIT_INNER.opacity,
                scale: INTRO2_ORBIT_INNER.scale,
              }
            : { opacity: 1, scale: 1 }
        }
        transition={
          play
            ? {
                duration: INTRO2_CYCLE_S,
                times: INTRO2_ORBIT_INNER.times,
                ease: [...cycleEase],
                repeat: Infinity,
              }
            : { duration: 0 }
        }
      />

      {/* Ed center — Figma 96344:109855；低于 tags */}
      <motion.div
        className={cn(
          "absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2",
          isDesktop ? "size-[93px]" : "size-[60px]",
        )}
        data-node-id={isDesktop ? "96344:109855" : "96301:11292"}
        data-testid="intro-slide-2-ed-center"
        initial={{ opacity: 0, scale: 0 }}
        animate={
          play
            ? {
                opacity: INTRO2_ED_CENTER_OPACITY.keyframes,
                scale: INTRO2_ED_CENTER_SCALE.keyframes,
              }
            : { opacity: 1, scale: 1 }
        }
        transition={
          play
            ? {
                opacity: {
                  duration: INTRO2_CYCLE_S,
                  times: INTRO2_ED_CENTER_OPACITY.times,
                  ease: ["easeOut", "linear"],
                  repeat: Infinity,
                },
                scale: {
                  duration: INTRO2_CYCLE_S,
                  times: INTRO2_ED_CENTER_SCALE.times,
                  ease: ["easeOut", "linear"],
                  repeat: Infinity,
                },
              }
            : { duration: 0 }
        }
      >
        <AppImage
          src={SIGN_IN_INTRO_ASSETS.edCenter}
          alt=""
          width={isDesktop ? 93 : 60}
          height={isDesktop ? 93 : 60}
          className="size-full object-contain"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = "none";
          }}
        />
      </motion.div>

      {/* Domain tags — 最上层 z-20；Figma 按方位先后点亮 */}
      <div className="pointer-events-none absolute inset-0 z-20">
        {DOMAIN_TAGS.map(({ key, icon, mobileClassName, desktopClassName }) => {
          const tagMotion = INTRO2_TAG_MOTION[key];
          const { x: fromX, y: fromY } = tagMotion.from;
          const times = tagMotion.opacityTimes;

          return (
            <motion.div
              key={key}
              className={cn(
                "absolute flex items-center rounded-lg bg-[rgba(5,41,43,0.9)]",
                isDesktop
                  ? "gap-2.5 px-[22px] py-3 body_medium_l"
                  : "gap-1.5 px-3.5 py-2 body_medium_m",
                isDesktop ? desktopClassName : mobileClassName,
              )}
              data-testid={`intro-slide-2-tag-${key}`}
              initial={{ opacity: 0, scale: 0.85, x: fromX, y: fromY }}
              animate={
                play
                  ? {
                      opacity: [0, 0, 1, 1],
                      scale: [...INTRO2_TAG_SCALE_IN],
                      x: [fromX, fromX, 0, 0],
                      y: [fromY, fromY, 0, 0],
                    }
                  : { opacity: 1, scale: 1, x: 0, y: 0 }
              }
              transition={
                play
                  ? {
                      duration: INTRO2_CYCLE_S,
                      times: [...times],
                      ease: [...cycleEase],
                      repeat: Infinity,
                    }
                  : { duration: 0 }
              }
            >
              <AppImage
                src={icon}
                alt=""
                width={isDesktop ? 25 : 16}
                height={isDesktop ? 25 : 16}
                className={cn("shrink-0", isDesktop ? "size-[25px]" : "size-4")}
              />
              <span className="truncate text-content_inverse_primary">
                {tags[key as keyof SignInIntroCopy["intro2"]["tags"]]}
              </span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};
